import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plane, Route as RouteIcon, Users, Settings2, BarChart3, Image as ImageIcon, CalendarRange, Handshake, FileText, Receipt, ShieldUser, Radar, AlertTriangle, Type, TrendingUp } from "lucide-react";
import { adminApi } from "../../lib/api";
import { StatsTab } from "../../components/admin/StatsTab";
import { ContentTab } from "../../components/admin/ContentTab";
import { SimpleCrudTab } from "../../components/admin/SimpleCrudTab";
import { ArticlesTab } from "../../components/admin/ArticlesTab";
import { AffiliatesTab } from "../../components/admin/AffiliatesTab";
import { BookingsTab } from "../../components/admin/BookingsTab";
import { UsersTab } from "../../components/admin/UsersTab";
import { LeadsTab } from "../../components/admin/LeadsTab";
import { SettingsTab } from "../../components/admin/SettingsTab";
import { ReachTab } from "../../components/admin/ReachTab";
import { CopyTab } from "../../components/admin/CopyTab";
import { MetaConnectTab } from "../../components/admin/MetaConnectTab";
import { B2BInsightsTab } from "../../components/admin/B2BInsightsTab";

const TABS = [
  { id: "stats", label: "Overview", icon: BarChart3 },
  { id: "airlines", label: "Airlines", icon: Plane },
  { id: "routes", label: "Routes", icon: RouteIcon },
  { id: "seasons", label: "Seasons", icon: CalendarRange },
  { id: "banners", label: "Banners", icon: ImageIcon },
  { id: "articles", label: "Articles", icon: FileText },
  { id: "affiliates", label: "Affiliates", icon: Handshake },
  { id: "copy", label: "Copy", icon: Type },
  { id: "reach", label: "Reach", icon: Radar },
  { id: "b2binsights", label: "B2B Insights", icon: TrendingUp },
  { id: "metaconnect", label: "MetaConnect", icon: Plane },
  { id: "bookings", label: "Bookings", icon: Receipt },
  { id: "users", label: "Users", icon: ShieldUser, ownerOnly: true },
  { id: "leads", label: "Leads", icon: Users },
  { id: "settings", label: "Settings", icon: Settings2 },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("stats");
  const [user, setUser] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("sv_admin_token")) navigate("/admin");
    try { setUser(JSON.parse(localStorage.getItem("sv_admin_user") || "null")); } catch { /* noop */ }
    adminApi().get("/admin/alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
  }, [navigate]);

  const role = user?.role || "owner";
  const visibleTabs = TABS.filter((t) => !t.ownerOnly || role === "owner");
  const roleColor = role === "owner" ? "bg-red-100 text-red-800" : role === "editor" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600";

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-40">
        <p className="font-heading font-bold text-slate-900">Sellvago Admin</p>
        <div className="flex items-center gap-3">
          {user && (
            <div className="hidden sm:flex items-center gap-2 text-xs" data-testid="admin-current-user">
              <span className="text-slate-500">{user.email || "recovery"}</span>
              <span className={`font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${roleColor}`} data-testid="admin-current-role">{role}</span>
            </div>
          )}
          <button
            data-testid="admin-logout-btn"
            onClick={() => { localStorage.removeItem("sv_admin_token"); localStorage.removeItem("sv_admin_user"); navigate("/admin"); }}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {alerts.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="admin-alerts-banner">
            {alerts.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-alert-${a.id}`}
                className={`flex items-start gap-3 border rounded-lg px-4 py-3 text-sm ${
                  a.severity === "red" ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"
                }`}
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-bold">{a.title}</p>
                  <p className="text-xs mt-0.5 opacity-90">{a.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {role === "viewer" && (
          <div className="mb-4 bg-slate-100 border border-slate-200 text-slate-700 text-xs px-4 py-2 rounded-lg" data-testid="admin-viewer-notice">
            You&apos;re signed in as <b>viewer</b> — read-only mode. Edit and delete actions are hidden.
          </div>
        )}
        <div className="flex gap-1 mb-8 bg-white border border-slate-200 rounded-lg p-1 max-w-full overflow-x-auto no-scrollbar">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
                tab === t.id ? "bg-[#970C10] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "stats" && <StatsTab />}
        {tab === "airlines" && <ContentTab kind="airline" />}
        {tab === "routes" && <ContentTab kind="route" />}
        {tab === "seasons" && <SimpleCrudTab kind="season" />}
        {tab === "banners" && <SimpleCrudTab kind="banner" />}
        {tab === "articles" && <ArticlesTab />}
        {tab === "affiliates" && <AffiliatesTab />}
        {tab === "reach" && <ReachTab />}
        {tab === "b2binsights" && <B2BInsightsTab />}
        {tab === "copy" && <CopyTab />}
        {tab === "metaconnect" && <MetaConnectTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
